import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarProps, Dropdown } from 'antd';
import datePickerLocaleEn from 'antd/es/date-picker/locale/en_US';
import datePickerLocaleEs from 'antd/es/date-picker/locale/es_ES';
import { PickerBaseProps } from 'antd/lib/date-picker/generatePicker';
import c from 'classnames';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { dayjs, Dayjs } from '@lgg/isomorphic/dayjs';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import {
  CalendarDateItem,
  CalendarHeader,
  DateBox,
  StyledCalendar,
} from 'src/components/general/inputs/custom-date-pickers/lgg-calendar';
import { LggDatePickerInput } from 'src/components/general/inputs/custom-date-pickers/lgg-date-picker-input';
import { ModalCloseIcon } from 'src/components/general/modals/modals.shared';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { ButtonsBar } from 'src/components/pages/conversations/components/general/buttons-bar';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useDateHelpers } from 'src/hooks/use-date-helpers';
import { useFormatDate } from 'src/hooks/use-format-date';
import { VisibilityHandler } from 'src/hooks/use-visible';

const CalendarContainer = styled.div`
  height: 286px;
  overflow: hidden;
  padding: 0 18px;

  .ant-picker-calendar .ant-picker-panel {
    border: none;
  }
`;

const HeaderContainer = styled(FlexRow)`
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  width: 100%;

  ${up('md')} {
    background: ${({ theme }) => theme.colors.alabaster};
    padding: 15px 20px;
  }
`;
const InputContainer = styled(FlexRow)`
  gap: 17px;
  width: 100%;

  & > * {
    flex: 1;
    max-width: 220px;
  }
`;

const StyledButtonsBar = styled(ButtonsBar)`
  border-top: 1px solid ${({ theme }) => theme.colors.koala};
  padding: 15px 20px;

  & > * {
    font-family: ${({ theme }) => theme.font.medium};
    font-size: 14px;
    line-height: 18px;
  }

  ${up('md')} {
    justify-content: flex-end;
    padding: 10px;

    & > * {
      font-family: ${({ theme }) => theme.font.medium};
      font-size: 12px;
      line-height: 18px;
      padding: 6px 15px;
      width: max-content;
    }

    * + * {
      margin-left: 10px;
    }
  }
`;

const DesktopOverlay = styled(FlexColumn)`
  border-radius: 6px;
  box-shadow: 0 20px 40px 0 rgba(91, 101, 112, 0.1);
  max-width: 564px;
  overflow: hidden;
  width: 100%;
`;

type LggRangePickerProps = {
  visibilityHandler: VisibilityHandler;
  onApply: (startDate: Dayjs, endDate: Dayjs) => void;
  startDateInit: Date | null;
  endDateInit: Date | null;
  disableDateOverride?: (date: Dayjs) => boolean;
  onSelectOverride?: (props: {
    setStartDate: ValueChanged<Dayjs | null>;
    setEndDate: ValueChanged<Dayjs | null>;
    setActivePicker: ValueChanged<'START' | 'END'>;
    selectedDate: Dayjs;
  }) => void;
  disableEndDate?: boolean;
  children: React.ReactNode;
  disableFutureDates?: boolean;
};

export const LggRangePicker = ({
  onApply,
  visibilityHandler,
  startDateInit,
  endDateInit,
  disableDateOverride,
  onSelectOverride,
  disableEndDate = false,
  children,
  disableFutureDates = true,
}: LggRangePickerProps) => {
  const breakpointUpMd = useBreakpoint(up('md'));
  const [activePicker, setActivePicker] = useState<'START' | 'END'>('START');
  const {
    t,
    i18n: { language },
  } = useTranslation(['dashboard', 'common']);
  const {
    initDayJsDate,
    isAfter,
    isBefore,
    isSameDay,
    isFuture,
    format,
    endOfDay,
    startOfDay,
    subMonths,
    addMonths,
  } = useDateHelpers();
  const [date, setDate] = useState<Dayjs>(initDayJsDate(startDateInit ?? new Date()));
  const [startDate, setStartDate] = useState<Dayjs | null>(
    startDateInit ? initDayJsDate(startDateInit) : null,
  );
  const { formatDateAndTimePickerDate } = useFormatDate();
  const [endDate, setEndDate] = useState<Dayjs | null>(
    endDateInit ? initDayJsDate(endDateInit) : null,
  );

  const onReset = useCallback(() => {
    setActivePicker('START');
    setStartDate(null);
    setEndDate(null);
    setDate(initDayJsDate(new Date()));
  }, [initDayJsDate]);

  useEffect(() => {
    if (!visibilityHandler.visible) {
      setActivePicker('START');
      setDate(startDateInit ? initDayJsDate(startDateInit) : initDayJsDate(new Date()));
      setStartDate(startDateInit ? initDayJsDate(startDateInit) : null);
      setEndDate(endDateInit ? initDayJsDate(endDateInit) : null);
    }
  }, [endDateInit, initDayJsDate, onReset, startDateInit, visibilityHandler.visible]);

  const datePickerLocale = useMemo(() => {
    if (language.startsWith('es')) {
      return datePickerLocaleEs;
    }

    return datePickerLocaleEn;
  }, [language]);

  const calendarProps: CalendarProps<Dayjs> = useMemo(
    () => ({
      fullscreen: false,
      headerRender: () => null,
      value: date,
      locale: datePickerLocale,
      disabledDate: disableDateOverride
        ? (date) => disableDateOverride(date)
        : (date) => {
            if (disableFutureDates && isFuture(date.toDate())) {
              return true;
            }

            if (activePicker === 'START') {
              return endDate ? isAfter(date.toDate(), endOfDay(endDate.toDate())) : false;
            }

            if (activePicker === 'END') {
              return startDate
                ? isBefore(date.toDate(), startOfDay(startDate.toDate()))
                : false;
            }

            return false;
          },
      onSelect: onSelectOverride
        ? (currDate) =>
            onSelectOverride({
              setEndDate,
              setActivePicker,
              setStartDate,
              selectedDate: initDayJsDate(currDate.toDate()),
            })
        : (currDate) => {
            if (activePicker === 'START') {
              setStartDate(initDayJsDate(startOfDay(currDate.toDate())));
              setActivePicker('END');
            }

            if (activePicker === 'END') {
              setEndDate(initDayJsDate(endOfDay(currDate.toDate())));

              if (startDate && isBefore(currDate.toDate(), startDate?.toDate())) {
                setStartDate(null);
                setActivePicker('START');
              }

              if (!startDate) {
                setActivePicker('START');
              }
            }
          },
      dateFullCellRender: (cellDate) => {
        const currentDateDateObject = cellDate.toDate();
        const showRange =
          startDate && endDate && !isSameDay(startDate.toDate(), endDate.toDate());

        return (
          <CalendarDateItem
            className={c({
              'in-range':
                startDate &&
                isAfter(currentDateDateObject, startDate?.toDate()) &&
                endDate &&
                isBefore(currentDateDateObject, endDate.toDate()),
              'show-range': showRange,
              'start-date':
                startDate &&
                isSameDay(startOfDay(currentDateDateObject), startDate.toDate()),
              'end-date':
                endDate && isSameDay(endOfDay(currentDateDateObject), endDate.toDate()),
            })}
          >
            <DateBox>{format(currentDateDateObject, 'D')}</DateBox>
          </CalendarDateItem>
        );
      },
    }),
    [
      activePicker,
      date,
      datePickerLocale,
      disableDateOverride,
      disableFutureDates,
      endDate,
      endOfDay,
      format,
      initDayJsDate,
      isAfter,
      isBefore,
      isFuture,
      isSameDay,
      onSelectOverride,
      startDate,
      startOfDay,
    ],
  );

  const footerButtons = useMemo(() => {
    return (
      <StyledButtonsBar
        primaryCallback={() => {
          if (startDate && endDate) {
            onApply(startDate, endDate);
            visibilityHandler.close();
          }
        }}
        secondaryCallback={onReset}
        primaryText={t('common:filters.actionsApply')}
        secondaryText={t('common:clear')}
        primaryButtonTestId="lgg-range-picker-apply-button"
        secondaryButtonTestId="lgg-range-picker-reset-button"
        disablePrimary={!startDate || !endDate}
      />
    );
  }, [endDate, onApply, onReset, startDate, t, visibilityHandler]);

  const inputs = useMemo(() => {
    const inputBaseProps: Partial<PickerBaseProps<dayjs.Dayjs>> = {
      format: (value) => formatDateAndTimePickerDate(value.toDate()),
    };

    return (
      <HeaderContainer>
        <InputContainer>
          <LggDatePickerInput
            {...inputBaseProps}
            onClick={() => {}}
            value={startDate}
            placeholderText={t('common:rangePickerInput.inputPlaceholders.fromDate')}
            onFocus={() => {
              setActivePicker('START');
            }}
            onChange={(value) => {
              if (value === null) {
                setStartDate(null);
              }
            }}
            data-lgg-id="range-picker-start-date-field"
            isActive={activePicker === 'START'}
          />
          <LggDatePickerInput
            {...inputBaseProps}
            onClick={() => {}}
            value={endDate}
            placeholderText={t('common:rangePickerInput.inputPlaceholders.toDate')}
            onFocus={() => {
              setActivePicker('END');
            }}
            onChange={(value) => {
              if (value === null) {
                setEndDate(null);
              }
            }}
            disabled={disableEndDate}
            data-lgg-id="range-picker-end-date-field"
            isActive={activePicker === 'END'}
          />
        </InputContainer>
        {breakpointUpMd ? (
          <ModalCloseIcon
            type="close"
            onClick={visibilityHandler.close}
            data-lgg-id="range-picker-close-icon"
          />
        ) : null}
      </HeaderContainer>
    );
  }, [
    activePicker,
    breakpointUpMd,
    disableEndDate,
    endDate,
    formatDateAndTimePickerDate,
    startDate,
    t,
    visibilityHandler.close,
  ]);

  return breakpointUpMd ? (
    <Dropdown
      overlayClassName="context-menu"
      visible={visibilityHandler.visible}
      onVisibleChange={(isVisible) => {
        if (!isVisible) {
          visibilityHandler.close();
        }
      }}
      placement="bottomLeft"
      trigger={['click']}
      overlay={
        <DesktopOverlay data-lgg-id="lgg-desktop-range-picker">
          <FlexRow>{inputs}</FlexRow>
          <FlexRow>
            <FlexColumn>
              <CalendarHeader
                headerDate={date.toDate()}
                onBack={() => setDate(initDayJsDate(subMonths(date.toDate(), 1)))}
                baseTestId="first-range"
                allowModeChange={false}
              />
              <StyledCalendar {...calendarProps} />
            </FlexColumn>
            <FlexColumn>
              <CalendarHeader
                headerDate={addMonths(date.toDate(), 1)}
                onNext={() => setDate(initDayJsDate(addMonths(date.toDate(), 1)))}
                baseTestId="second-range"
                allowModeChange={false}
              />
              <StyledCalendar
                {...calendarProps}
                value={
                  calendarProps.value
                    ? initDayJsDate(addMonths(calendarProps.value?.toDate(), 1))
                    : undefined
                }
              />
            </FlexColumn>
          </FlexRow>
          {footerButtons}
        </DesktopOverlay>
      }
    >
      {children}
    </Dropdown>
  ) : (
    <>
      {children}
      <BottomDrawer
        visible={visibilityHandler.visible}
        title={t('common:rangePickerInput.title')}
        onClose={visibilityHandler.close}
        footer={footerButtons}
        data-lgg-id="lgg-mobile-range-picker"
      >
        {inputs}

        <CalendarHeader
          headerDate={date.toDate()}
          onNext={() => setDate(initDayJsDate(addMonths(date.toDate(), 1)))}
          onBack={() => setDate(initDayJsDate(subMonths(date.toDate(), 1)))}
          baseTestId="mobile-range"
          allowModeChange={false}
        />

        <CalendarContainer>
          <StyledCalendar {...calendarProps} />
        </CalendarContainer>
      </BottomDrawer>
    </>
  );
};
